<template>
  <section
    class="media-resources-module-component module-container"
    :style="{
      '--media-resources-underlay': props.data?.underlay_color || '#1E6B5C',
    }">
    <div
      class="media-resources-surface"
      aria-hidden="true"></div>

    <div class="media-resources-inner">
      <div
        ref="filtersTop"
        class="resource-filters">
        <div>
          <div
            v-if="data.text_above_filters"
            class="text-above mb-10"
            v-html="data.text_above_filters"></div>

          <form
            class="resource-filter-controls"
            @submit.prevent="applyFilters">
            <MediaResourcesFilter
              v-model="pendingCategory"
              label="Category"
              :options="categoryOptions" />

            <MediaResourcesFilter
              v-model="pendingType"
              label="Content Type"
              :options="typeOptions" />

            <div class="filter-actions">
              <button
                class="apply-filters"
                type="submit">
                Apply Filters
              </button>
              <button
                class="clear-filters"
                type="button"
                @click="clearFilters">
                Clear Filters
              </button>
            </div>
          </form>
        </div>

        <aside
          v-if="showContactBox"
          class="media-contact-card">
          <h2>{{ contactDetails.title }}</h2>
          <a
            v-if="contactDetails.email"
            :href="`mailto:${contactDetails.email}`">
            {{ contactDetails.email }}
          </a>
          <div
            v-if="contactDetails.copy"
            class="media-contact-copy"
            v-html="contactDetails.copy"></div>
        </aside>
      </div>

      <div class="results-heading-row">
        <h2
          class="results-heading"
          aria-live="polite">
          <template v-if="hasActiveFilters">
            Showing {{ filteredResources.length }}
            {{ filteredResources.length === 1 ? 'Result' : 'Results' }}
          </template>
          <template v-else>Showing All Results</template>
        </h2>
      </div>

      <div
        v-if="isLoading"
        class="resource-grid"
        aria-label="Loading media resources"
        aria-busy="true">
        <article
          v-for="item in perPage"
          :key="`media-resource-skeleton-${item}`"
          class="resource-card resource-card-skeleton">
          <div class="resource-preview skeleton-block"></div>
          <div class="skeleton-line short"></div>
          <div class="skeleton-line"></div>
          <div class="skeleton-actions">
            <span></span><span></span><span></span>
          </div>
        </article>
      </div>

      <p
        v-else-if="errorMessage"
        class="resource-message"
        role="alert">
        {{ errorMessage }}
      </p>

      <p
        v-else-if="!filteredResources.length"
        class="resource-message">
        {{ emptyMessage }}
      </p>

      <div
        v-else
        class="resource-grid">
        <article
          v-for="resource in paginatedResources"
          :key="resource.id"
          class="resource-card">
          <div class="resource-card-content">
            <button
              class="resource-preview"
              type="button"
              :aria-label="`Preview ${plainText(resource.title)}`"
              @click="openPreview(resource)">
              <img
                v-if="resource.preview_url"
                :src="resource.preview_url"
                :alt="plainText(resource.title)"
                loading="lazy" />
              <span
                v-else
                class="resource-placeholder"
                aria-hidden="true">
                {{ typeLabel(resource.type) }}
              </span>
            </button>

            <div class="resource-tags">
              <span
                v-for="category in resource.categories"
                :key="`${resource.id}-${category.slug}`">
                {{ category.name }}
              </span>
              <span>{{ typeLabel(resource.type) }}</span>
            </div>

            <h3
              class="resource-title"
              v-html="resource.title"></h3>
          </div>

          <div class="resource-card-actions">
            <div class="resource-buttons">
              <button
                class="resource-button preview-button"
                type="button"
                @click="openPreview(resource)">
                Preview
              </button>
              <a
                class="resource-button download-button no-router"
                :href="resource.file_url"
                :download="resource.file_name">
                Download
                <svg
                  aria-hidden="true"
                  viewBox="0 0 16 16">
                  <path d="M8 2v8m0 0 3-3m-3 3L5 7M3 13h10" />
                </svg>
              </a>
            </div>
            <span
              v-if="resource.file_size_label"
              class="file-size">
              {{ resource.file_size_label }}
            </span>
          </div>
        </article>
      </div>

      <nav
        v-if="totalPages > 1"
        class="resource-pagination"
        aria-label="Media resources pagination">
        <button
          type="button"
          aria-label="Previous page"
          :disabled="currentPage === 1"
          @click="goToPage(currentPage - 1)">
          <svg
            aria-hidden="true"
            viewBox="0 0 20 20">
            <path d="m12.5 5-5 5 5 5" />
          </svg>
        </button>
        <button
          v-for="page in totalPages"
          :key="`media-resource-page-${page}`"
          type="button"
          :class="{ active: currentPage === page }"
          :aria-label="`Page ${page}`"
          :aria-current="currentPage === page ? 'page' : null"
          @click="goToPage(page)">
          {{ page }}
        </button>
        <button
          type="button"
          aria-label="Next page"
          :disabled="currentPage === totalPages"
          @click="goToPage(currentPage + 1)">
          <svg
            aria-hidden="true"
            viewBox="0 0 20 20">
            <path d="m7.5 5 5 5-5 5" />
          </svg>
        </button>
      </nav>
    </div>

    <Teleport to="body">
      <Transition name="resource-modal-fade">
        <div
          v-if="activeResource"
          class="resource-modal"
          role="dialog"
          aria-modal="true"
          :aria-label="`Preview ${plainText(activeResource.title)}`"
          @click.self="closePreview">
          <div class="resource-modal-content">
            <button
              ref="modalClose"
              class="resource-modal-close"
              type="button"
              aria-label="Close preview"
              @click="closePreview">
              <span></span><span></span>
            </button>

            <div class="resource-modal-viewer">
              <img
                v-if="activeResource.type === 'image'"
                :src="activeResource.file_url"
                :alt="plainText(activeResource.title)" />
              <video
                v-else-if="activeResource.type === 'video'"
                :poster="activeResource.preview_url || null"
                controls
                autoplay>
                <source
                  :src="activeResource.file_url"
                  :type="activeResource.mime_type" />
                Your browser does not support embedded video.
              </video>
              <iframe
                v-else
                :src="activeResource.file_url"
                :title="plainText(activeResource.title)"></iframe>
            </div>

            <div class="resource-modal-footer">
              <h2 v-html="activeResource.title"></h2>
              <a
                class="resource-button download-button"
                :href="activeResource.file_url"
                :download="activeResource.file_name">
                Download
                <svg
                  aria-hidden="true"
                  viewBox="0 0 16 16">
                  <path d="M8 2v8m0 0 3-3m-3 3L5 7M3 13h10" />
                </svg>
              </a>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>
  </section>
</template>

<script setup>
import {
  computed,
  nextTick,
  onBeforeMount,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from 'vue'
import { useStore } from '@/stores/main'
import MediaResourcesFilter from '@/components/modules/MediaResourcesFilter.vue'
import useSmoothScroll from '@/components/composables/useSmoothScroll'

const props = defineProps(['data'])
const store = useStore()
const { locoScroll } = useSmoothScroll()

const resources = ref([])
const isLoading = ref(true)
const errorMessage = ref('')
const pendingCategory = ref('all')
const pendingType = ref('all')
const appliedCategory = ref('all')
const appliedType = ref('all')
const currentPage = ref(1)
const activeResource = ref(null)
const modalClose = ref(null)
const filtersTop = ref(null)
let previousBodyOverflow = ''

const perPage = computed(() => {
  const configured = Number(props.data?.per_page)
  return configured > 0 ? configured : 9
})

const emptyMessage = computed(
  () =>
    props.data?.empty_message ||
    'No media resources found with the selected filters.'
)

const showContactBox = computed(
  () => ![false, 0, '0'].includes(props.data?.show_contact_box)
)

const contactDetails = computed(() => ({
  title: props.data?.contact_title || 'Media Contacts',
  email: props.data?.contact_email || 'media@revmed.com',
  copy:
    props.data?.contact_copy ||
    '<p>For additional contact information, please visit the <a href="/contact-us/">Contact Us</a> page.</p>',
}))

const availableCategories = computed(() => {
  const categories = new Map()

  resources.value.forEach((resource) => {
    resource.categories.forEach((category) => {
      categories.set(category.slug, category)
    })
  })

  return [...categories.values()].sort((a, b) =>
    a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
  )
})

const availableTypes = computed(() => {
  const order = ['image', 'video', 'pdf']
  const types = new Set(resources.value.map((resource) => resource.type))
  return order.filter((type) => types.has(type))
})

const categoryOptions = computed(() => [
  { value: 'all', label: 'All Categories' },
  ...availableCategories.value.map((category) => ({
    value: category.slug,
    label: category.name,
  })),
])

const typeOptions = computed(() => [
  { value: 'all', label: 'All Types' },
  ...availableTypes.value.map((type) => ({
    value: type,
    label: typeLabel(type),
  })),
])

const filteredResources = computed(() =>
  resources.value.filter((resource) => {
    const categoryMatches =
      appliedCategory.value === 'all' ||
      resource.categories.some(
        (category) => category.slug === appliedCategory.value
      )
    const typeMatches =
      appliedType.value === 'all' || resource.type === appliedType.value

    return categoryMatches && typeMatches
  })
)

const hasActiveFilters = computed(
  () => appliedCategory.value !== 'all' || appliedType.value !== 'all'
)

const totalPages = computed(() =>
  Math.ceil(filteredResources.value.length / perPage.value)
)

const paginatedResources = computed(() => {
  const start = (currentPage.value - 1) * perPage.value
  return filteredResources.value.slice(start, start + perPage.value)
})

const requestItems = (params) => {
  const request = { type: 'media_resource', params }
  return store.getItems(request).then(() => ({
    items: store.requestedItems(request),
    request: store.request(request),
  }))
}

const loadResources = async () => {
  isLoading.value = true
  errorMessage.value = ''

  try {
    const baseParams = {
      per_page: 100,
      orderby: 'menu_order',
      order: 'asc',
    }
    const firstPage = await requestItems(baseParams)
    const loadedResources = [...firstPage.items]
    const requestPages = Number(firstPage.request?.totalPages || 1)

    for (let page = 2; page <= requestPages; page += 1) {
      const nextPage = await requestItems({ ...baseParams, page })
      loadedResources.push(...nextPage.items)
    }

    resources.value = loadedResources
      .filter((item) => item.resource_data?.file_url)
      .map((item) => ({
        id: item.id,
        title: item.title?.rendered || '',
        ...item.resource_data,
        categories: item.resource_data.categories || [],
      }))
  } catch (error) {
    console.error('Unable to load media resources.', error)
    errorMessage.value =
      'Media resources are temporarily unavailable. Please try again later.'
  } finally {
    isLoading.value = false
  }
}

const applyFilters = () => {
  appliedCategory.value = pendingCategory.value
  appliedType.value = pendingType.value
  currentPage.value = 1
}

const clearFilters = () => {
  pendingCategory.value = 'all'
  pendingType.value = 'all'
  appliedCategory.value = 'all'
  appliedType.value = 'all'
  currentPage.value = 1
}

const goToPage = (page) => {
  if (page < 1 || page > totalPages.value || page === currentPage.value) return
  currentPage.value = page

  nextTick(() => {
    window.requestAnimationFrame(() => {
      if (!filtersTop.value) return

      const top = Math.max(
        0,
        window.scrollY + filtersTop.value.getBoundingClientRect().top - 110
      )

      if (locoScroll.value?.scrollTo) {
        locoScroll.value.scrollTo(top, { duration: 0.8, force: true })
        return
      }

      window.scrollTo({ top, behavior: 'smooth' })
    })
  })
}

const typeLabel = (type) =>
  type === 'pdf' ? 'PDF' : `${type[0].toUpperCase()}${type.slice(1)}`
const plainText = (value) => String(value || '').replace(/<[^>]*>/g, '')

const openPreview = (resource) => {
  activeResource.value = resource
  nextTick(() => modalClose.value?.focus())
}

const closePreview = () => {
  activeResource.value = null
}

const handleKeydown = (event) => {
  if (event.key === 'Escape' && activeResource.value) closePreview()
}

watch(activeResource, (resource) => {
  if (resource) {
    previousBodyOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
  } else {
    document.body.style.overflow = previousBodyOverflow
  }
})

watch(totalPages, (pages) => {
  if (pages && currentPage.value > pages) currentPage.value = pages
})

onBeforeMount(loadResources)
onMounted(() => window.addEventListener('keydown', handleKeydown))
onBeforeUnmount(() => {
  window.removeEventListener('keydown', handleKeydown)
  document.body.style.overflow = previousBodyOverflow
})
</script>

<style lang="scss" scoped>
/* Media Resources layout mirrors the approved Figma frame. */
.media-resources-module-component {
  @apply relative z-3 w-full overflow-hidden px-5 pb-12 md:px-10 lg:px-14 lg:pb-[70px];

  .media-resources-surface {
    @apply pointer-events-none absolute inset-x-0 bottom-0 top-0 z-0 bg-white;
  }

  .media-resources-inner {
    @apply relative z-1 mx-auto w-full max-w-[1200px];

    .resource-filters {
      @apply mb-12 flex flex-col justify-between gap-10;
      @apply lg:mb-[50px] lg:min-h-[95px] lg:flex-row lg:items-end xl:gap-[70px];

      .text-above {
        @apply flex flex-col gap-5;
      }

      .resource-filter-controls {
        @apply order-2 flex flex-col gap-3 md:flex-row md:flex-wrap md:items-end lg:order-1;

        .filter-actions {
          @apply mt-2 flex items-center gap-3 md:mt-0;

          button {
            @apply whitespace-nowrap rounded-full px-6 py-[13px] text-[18px] leading-tight transition-colors;
          }

          .apply-filters {
            @apply bg-progressive text-humble hover:bg-balanced hover:text-white;
          }

          .clear-filters {
            @apply bg-functional text-humble hover:bg-balanced hover:text-white;
          }
        }
      }

      .media-contact-card {
        @apply relative order-1 w-full rounded-[15px] bg-balanced p-6 text-white lg:order-2 lg:w-[310px] lg:shrink-0;

        h2 {
          @apply mb-2 text-[22px] font-normal leading-tight text-progressive;
        }

        > a {
          @apply inline-block text-[16px] leading-tight text-white no-underline transition-all hover:underline;
        }

        .media-contact-copy {
          @apply mt-3 text-[11px] leading-[1.25] text-white;

          :deep(p) {
            @apply m-0 text-[16px] text-white;
          }

          :deep(a) {
            @apply text-white underline underline-offset-2;
          }
        }
      }
    }

    .results-heading-row {
      @apply mb-7 lg:mb-[50px];

      .results-heading {
        @apply font-FKDisplay text-[26px] font-normal leading-tight tracking-[-0.02em] text-balanced lg:text-[40px];
      }
    }

    .resource-grid {
      @apply grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3;

      .resource-card {
        @apply flex min-w-0 flex-col rounded-[20px] bg-functional p-4 lg:min-h-[450px];

        .resource-card-content {
          @apply flex flex-col gap-2 lg:gap-5;

          .resource-preview {
            @apply flex aspect-[16/10] w-full items-center justify-center overflow-hidden rounded-[14px] border-0 bg-[#d4d4d4] p-0;

            img {
              @apply h-full w-full object-cover transition-transform duration-300;
            }

            &:hover img,
            &:focus-visible img {
              @apply scale-[1.03];
            }

            .resource-placeholder {
              @apply text-[14px] font-medium uppercase tracking-[0.08em] text-gray_green;
            }
          }

          .resource-tags {
            @apply flex flex-wrap gap-[10px];

            span {
              @apply rounded-[10px] bg-white p-2 text-[13px] leading-none text-balanced;
            }
          }

          .resource-title {
            @apply mb-2 text-[22px] font-medium leading-[1.2] tracking-[-0.02em] text-balanced;
          }
        }

        .resource-card-actions {
          @apply mt-auto flex flex-wrap items-center gap-x-10 gap-y-[10px] justify-between;

          .resource-buttons {
            @apply flex flex-row flex-wrap gap-[10px];

            .resource-button {
              @apply inline-flex items-center justify-center gap-1 rounded-full px-4 py-1.5 text-[16px] leading-tight no-underline transition-colors;

              svg {
                @apply h-4 w-4;

                path {
                  fill: none;
                  stroke: currentColor;
                  stroke-linecap: round;
                  stroke-linejoin: round;
                  stroke-width: 1.4;
                }
              }
            }
          }

          .preview-button {
            @apply bg-balanced text-progressive hover:bg-humble;
          }

          .download-button {
            @apply bg-progressive text-humble hover:bg-balanced hover:text-white;
          }

          .file-size {
            @apply rounded-[10px] border border-balanced px-2 py-[7px] text-[12px] leading-tight text-balanced;
          }
        }

        &.resource-card-skeleton {
          @apply min-h-[390px];

          .resource-preview {
            @apply mb-4 aspect-[16/10] w-full rounded-[14px];
          }

          .skeleton-block,
          .skeleton-line,
          .skeleton-actions span {
            @apply animate-pulse bg-[#d7dcdb];
          }

          .skeleton-line {
            @apply mb-3 h-4 w-full rounded;

            &.short {
              @apply w-1/3;
            }
          }

          .skeleton-actions {
            @apply mt-auto flex gap-2;

            span {
              @apply h-[30px] flex-1 rounded-full;

              &:last-child {
                @apply max-w-[58px];
              }
            }
          }
        }
      }
    }

    .resource-message {
      @apply rounded-[20px] bg-functional px-6 py-12 text-center text-[18px] text-balanced;
    }

    .resource-pagination {
      @apply mx-auto mt-12 flex w-fit items-center gap-1.5 rounded-[14px] border-[5px] border-gray_green_trans bg-functional px-3 py-2 lg:mt-[50px];

      button {
        @apply flex h-8 min-w-[32px] items-center justify-center rounded-full border-0 bg-transparent px-2 text-[13px] text-humble transition-colors hover:bg-white disabled:pointer-events-none disabled:opacity-30;

        &.active {
          @apply border border-balanced bg-white;
        }

        svg {
          @apply h-5 w-5 text-progressive;

          path {
            fill: none;
            stroke: currentColor;
            stroke-linecap: round;
            stroke-linejoin: round;
            stroke-width: 1.5;
          }
        }
      }
    }
  }
}

.resource-modal-fade {
  &-enter-active,
  &-leave-active {
    @apply transition-opacity duration-200;
  }

  &-enter-from,
  &-leave-to {
    @apply opacity-0;
  }
}
</style>

<style lang="scss">
.resource-modal {
  @apply fixed inset-0 z-[101] flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm md:p-8;

  .resource-modal-content {
    @apply relative flex max-h-full w-full max-w-[1100px] flex-col overflow-hidden rounded-[20px] bg-white shadow-2xl;

    .resource-modal-close {
      @apply absolute right-3 top-3 z-2 flex h-11 w-11 items-center justify-center rounded-full border border-balanced bg-white transition-colors hover:bg-balanced;

      span {
        @apply absolute h-[1px] w-5 bg-balanced transition-colors;

        &:first-child {
          @apply rotate-45;
        }

        &:last-child {
          @apply -rotate-45;
        }
      }

      &:hover span {
        @apply bg-white;
      }
    }

    .resource-modal-viewer {
      @apply flex min-h-[260px] flex-1 items-center justify-center overflow-auto bg-[#dfe3e2] p-4 md:min-h-[520px];

      img,
      video {
        @apply max-h-[70vh] max-w-full rounded-[10px] object-contain;
      }

      iframe {
        @apply h-[70vh] min-h-[420px] w-full rounded-[10px] border-0 bg-white;

        @media (max-width: 639px) {
          min-height: 55vh;
        }
      }
    }

    .resource-modal-footer {
      @apply flex flex-col gap-4 px-5 py-5 pr-16 sm:flex-row sm:items-center sm:justify-between md:px-8;

      h2 {
        @apply text-[20px] font-medium leading-tight text-balanced md:text-[24px];
      }

      a {
        &.resource-button.download-button {
          @apply flex gap-[10px] items-center;

          svg {
            @apply h-4 w-4 text-current;

            path {
              fill: none;
              stroke: currentColor;
              stroke-linecap: round;
              stroke-linejoin: round;
              stroke-width: 1.4;
            }
          }
        }
      }
    }
  }
}
</style>
